import client from 'client'
import type {
  GetReportJobListReportGetQueryResponse,
  GetReportJobListReportGetQueryParams,
  GetReportJobListReportGet422,
} from '../../types/GetReportJobListReportGet.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getReportJobListReportGetQueryKey = (params?: GetReportJobListReportGetQueryParams) => [{ url: '/report' }, ...(params ? [params] : [])] as const

export type GetReportJobListReportGetQueryKey = ReturnType<typeof getReportJobListReportGetQueryKey>

/**
 * @summary Get Report Job List
 * {@link /report}
 */
export async function getReportJobListReportGet(
  params?: GetReportJobListReportGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<GetReportJobListReportGetQueryResponse, ResponseErrorConfig<GetReportJobListReportGet422>, unknown>({
    method: 'GET',
    url: `/report`,
    params,
    ...requestConfig,
  })
  return res
}

export function getReportJobListReportGetQueryOptions(
  params?: GetReportJobListReportGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const queryKey = getReportJobListReportGetQueryKey(params)
  return queryOptions<
    ResponseConfig<GetReportJobListReportGetQueryResponse>,
    ResponseErrorConfig<GetReportJobListReportGet422>,
    ResponseConfig<GetReportJobListReportGetQueryResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getReportJobListReportGet(params, config)
    },
  })
}

/**
 * @summary Get Report Job List
 * {@link /report}
 */
export function useGetReportJobListReportGet<
  TData = ResponseConfig<GetReportJobListReportGetQueryResponse>,
  TQueryData = ResponseConfig<GetReportJobListReportGetQueryResponse>,
  TQueryKey extends QueryKey = GetReportJobListReportGetQueryKey,
>(
  params?: GetReportJobListReportGetQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetReportJobListReportGetQueryResponse>,
        ResponseErrorConfig<GetReportJobListReportGet422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getReportJobListReportGetQueryKey(params)

  const query = useQuery({
    ...(getReportJobListReportGetQueryOptions(params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetReportJobListReportGet422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}